import AHDate from '_shared/dates/date';
import { useDraggable } from '@dnd-kit/core';
import AssistantIcon from "@mui/icons-material/Assistant";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepIcon from '@mui/icons-material/DeleteSweepOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import LastPageIcon from '@mui/icons-material/LastPage';
import SendIcon from "@mui/icons-material/Send";
import StopCircleIcon from "@mui/icons-material/StopCircleOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from "@mui/material/Fab";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import type { PopperPlacementType, PopperProps } from "@mui/material/Popper";
import Popper from "@mui/material/Popper";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { ConfirmResponse, useConfirmDialog } from '_shared/_providers/dialogs/confirm/confirm-dialog';
import { KeyboardKey } from '_shared/_utilities/keyboard-keys';
import { ResizeHandlePlacementContext, ResizeHandle } from '_shared/_views/handles/resize-handle';
import {
  useCancelStagedActionMutation,
  useConfirmStagedActionMutation,
  useDeleteAllAssistantConversationsMutation,
  useLazyReadAssistantConversationQuery,
  useListAssistantConversationsQuery,
  useSendAssistantMessageMutation
} from 'ai-assistant/_api/ai-assistant-api-service';
import { MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS } from 'ai-assistant/_api/commands/assistant-api-command';
import AssistantConversation from 'ai-assistant/conversations/assistant-conversation';
import AssistantResponse from 'ai-assistant/assistant-response';
import CitedAnswer from 'ai-assistant/citations/_views/cited-answer';
import StagedActionCard from 'ai-assistant/staged-actions/_views/staged-action-card';
import {
  describeResolutionFailure,
  StagedActionIntent,
  StagedActionResolutionFailure
} from 'ai-assistant/staged-actions/staged-action-resolution-failure';
import React from "react";
import { useLocation } from 'react-router';
import { useSession } from 'users/__session/session-context';
import { Role } from 'ai-assistant/roles/assistant-role';

/** What each card has been told about an answer that never arrived, keyed by action id. */
type ResolutionFailures = Readonly<Record<string, StagedActionResolutionFailure>>;

/**
 * Whether a rejected turn was stopped by the user rather than broken.
 *
 * RTK Query rejects an aborted request with an `AbortError`, which is the only signal there
 * is: the request never reached a response, so there is no status to read. Module scope
 * because the distinction is worth stating once - everything else about the two cases is
 * identical, and treating a deliberate stop as an error puts it in the console and tells the
 * user something went wrong when nothing did.
 */
function wasStoppedByUser(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && 'name' in error
    && (error as { name: unknown }).name === 'AbortError';
}

/**
 * Everything inside the panel a Tab can reach, in the order Tab reaches it.
 *
 * `querySelectorAll` answers in document order, which is tab order here because nothing in the
 * panel carries a positive `tabIndex`. `tabIndex="-1"` is excluded: the panel itself and each
 * prepared action's card are focusable so that focus can be *sent* to them, and neither belongs
 * in the cycle a user tabs through.
 *
 * Deliberately not filtered on visibility. Everything here is either rendered or not - the panel
 * has no hidden-but-present controls - and the usual test for it, `offsetParent`, reports every
 * element in jsdom as invisible, which would silently turn the trap off under test.
 */
const TabbableSelector = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]',
].map(candidate => `${candidate}:not([disabled]):not([tabindex="-1"])`).join(',');

function tabbableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(TabbableSelector));
}

/**
 * What the panel says out loud about a turn, for a reader who cannot see it arrive.
 *
 * One sentence at a time, rendered into a single polite live region. A screen reader speaks a
 * live region when its content *changes*, so this returns a value derived from state rather
 * than pushing announcements: "working" and then the answer are two different strings, so the
 * arrival is spoken without anything having to remember that it fired.
 *
 * A finished answer is announced with its text. The alternative - "the assistant answered" and
 * nothing more - leaves a blind user to go hunting through the transcript for the reply to a
 * question they just asked, every single turn. It also keeps consecutive turns distinguishable:
 * two identical sentences in a row are one silent announcement.
 *
 * A turn that staged an action is the exception, and is announced without its text. Focus moves
 * to the prepared action's card in that case (see `decisionToFocus`), and the reader speaks a
 * focus change ahead of a polite region - so reading the answer here would either be interrupted
 * or queued behind the thing it is meant to introduce.
 *
 * Two kinds of turn are deliberately silent here, because they already speak for themselves: a
 * failure renders as `role="alert"`, and a stopped turn as a `role="status"` note.
 */
function announcementFor(messages: AssistantResponse[], showResponding: boolean): string {
  if (showResponding) return 'The assistant is working on your answer.';

  const latest = messages[messages.length - 1];
  if (latest === undefined) return '';
  if (latest.role === Role.User) return '';
  if (!latest.showsAnswerText) return '';

  if (latest.actionableStagedActions.some(action => action.isPending)) {
    return 'The assistant answered, and prepared an action for you to approve.';
  }

  return `The assistant answered. ${latest.text}`;
}

/** The prepared action a turn has just put in front of the user, if it put one there. */
function decisionStagedBy(reply: AssistantResponse): string | undefined {
  return reply.actionableStagedActions.find(action => action.isPending)?.id.value;
}

/**
 * Confirms or cancels a prepared action and appends the assistant's reply.
 *
 * Module scope on purpose. `app/CLAUDE.md` bans `try/finally` inside a component because the
 * React Compiler cannot lower one and silently stops memoizing the whole component - and the
 * component this belongs to already carries that scar in `handleSendMessage`. Module-level
 * functions are not compiled, so the control flow here survives exactly as written.
 *
 * A refusal is an answer, not an error: the server replies 200 with prose explaining that the
 * action expired or was already handled, and that reply is appended like any other. What reaches
 * the catch is a request that produced no answer at all, and the card it was sent from has to
 * say so - `console.error` alone left a 403, a dropped connection and a timeout all rendering
 * as nothing having happened.
 */
async function resolveStagedAction(
  resolve: (request: { stagedActionId: string }) => { unwrap: () => Promise<AssistantResponse> },
  stagedActionId: string,
  intent: StagedActionIntent,
  setMessages: React.Dispatch<React.SetStateAction<AssistantResponse[]>>,
  setResolvingActionIds: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>,
  setResolutionFailures: React.Dispatch<React.SetStateAction<ResolutionFailures>>
): Promise<void> {
  try {
    const reply = await resolve({ stagedActionId }).unwrap();
    setMessages(previous => appendResolution(previous, reply));
  } catch (error) {
    console.error('Error resolving a staged assistant action:', error);
    setResolutionFailures(previous => ({
      ...previous,
      [stagedActionId]: describeResolutionFailure(error, intent),
    }));
  } finally {
    // This id only. Two resolutions really can be in flight at once, so a set rather than a
    // slot: one request finishing must not speak for another, and one starting must not clear
    // another's spinner and re-enable a Confirm whose own request is still running.
    setResolvingActionIds(previous => withoutResolvingAction(previous, stagedActionId));
  }
}

/**
 * Loads a conversation the server already holds into the panel.
 *
 * The transcript used to exist only in this component's state, so a refresh lost it while the
 * server kept every turn indefinitely - a user could see none of their own assistant history and
 * delete none of it either. See #4658.
 *
 * Module scope for the reason `resolveStagedAction` is: `app/CLAUDE.md` bans `try/finally` and a
 * `throw` inside a `try` in a component because the React Compiler cannot lower either and
 * silently stops memoizing the whole component.
 *
 * A failure here is not worth surfacing. The user opened a panel; the worst case is that they
 * start a new conversation instead of continuing the last one, which is what happened before this
 * existed.
 */
async function hydrateConversation(
  conversationId: string,
  read: (request: { conversationId: string }) => { unwrap: () => Promise<AssistantConversation> },
  setMessages: React.Dispatch<React.SetStateAction<AssistantResponse[]>>,
  setConversationId: React.Dispatch<React.SetStateAction<string | undefined>>
): Promise<void> {
  try {
    const conversation = await read({ conversationId }).unwrap();
    // Only into a panel nobody has used yet. This read is asynchronous and the composer works
    // throughout it, so somebody who opens the assistant and types straight away has a turn on
    // screen before the answer arrives. Replacing the transcript then erased the question they
    // had just asked and whatever came back with it — a prepared action among it — and
    // left them reading a conversation they never opened, under a banner calling it history.
    // A panel with anything in it is a conversation of its own, and history stays where it is.
    setMessages(previous => (previous.length > 0 ? previous : conversation.toTranscript()));
    setConversationId(current => current ?? conversation.conversationId);
  } catch (error) {
    console.error('Error loading an assistant conversation:', error);
  }
}

/**
 * Erases the user's assistant history, on the server and on screen.
 *
 * Conversations under legal hold survive, and the user is told when any did rather than being
 * shown an empty panel that implies everything is gone.
 */
async function clearAssistantHistory(
  erase: () => { unwrap: () => Promise<{ deleted: number; retainedUnderLegalHold: number }> },
  setMessages: React.Dispatch<React.SetStateAction<AssistantResponse[]>>,
  setConversationId: React.Dispatch<React.SetStateAction<string | undefined>>,
  setNotice: React.Dispatch<React.SetStateAction<string | undefined>>
): Promise<void> {
  try {
    const erasure = await erase().unwrap();
    setMessages([]);
    setConversationId(undefined);
    setNotice(
      erasure.retainedUnderLegalHold > 0
        ? `Deleted ${erasure.deleted} conversation(s). `
          + `${erasure.retainedUnderLegalHold} are preserved and could not be deleted.`
        : undefined
    );
  } catch (error) {
    console.error('Error deleting assistant conversations:', error);
    setNotice('Your conversations could not be deleted. Please try again.');
  }
}

/**
 * Folds a resolution into the transcript.
 *
 * The server answers a confirm or a cancel with the prepared action in the state it moved it
 * to, so the card the user clicked is redrawn where it already sits and the copy travelling
 * with the reply is dropped. Appending the reply as it arrived would leave the original card
 * still offering Confirm and Cancel for work that is already done, and would show two cards
 * for one action id.
 */
function appendResolution(
  messages: AssistantResponse[],
  reply: AssistantResponse
): AssistantResponse[] {
  let transcript = messages;
  for (const resolved of reply.stagedActions) {
    transcript = transcript.map(message => message.withStagedActionResolved(resolved));
  }

  return [...transcript, reply.withoutStagedActions()];
}

/**
 * Appends a turn, retiring any preview the user left unanswered.
 *
 * Staging a new action supersedes the outstanding ones in that conversation on the server, in
 * the same transaction. The reply says nothing about it - a turn carries only what it staged -
 * so the transcript has to apply the same rule, or a card from three messages ago goes on
 * offering a Confirm whose only remaining answer is a refusal. A turn that stages nothing
 * retires nothing: an unanswered preview outlives an ordinary reply.
 *
 * `confirmed` is every action this panel has sent a confirmation for, and none of them is
 * retired here whatever the transcript still says about them. The server does not supersede an
 * action it is executing, and a card whose confirmation came back uncertain is the one card in
 * the panel that must go on saying so - relabelling it "something newer replaced this" would
 * withdraw an honest "I cannot tell whether that went through" about a write that may have
 * happened. Their state is the server's to report, in the reply to the confirmation they were
 * sent.
 *
 * A cancellation is deliberately not in that set. A cancel writes no business data, so a cancel
 * whose request never arrived - the one failure the panel offers again - leaves the action in
 * whatever state the server has it in, and once a newer preview exists that state is superseded.
 * Retaining it there would leave a card still offering Confirm and Cancel for an action the
 * server has already retired, which is the stale card this function exists to prevent.
 */
function appendTurn(
  messages: AssistantResponse[],
  reply: AssistantResponse,
  confirmed: ReadonlySet<string>
): AssistantResponse[] {
  if (reply.stagedActions.length === 0) return [...messages, reply];

  return [
    ...messages.map(message => message.withPendingStagedActionsSuperseded(confirmed)),
    reply,
  ];
}

/** The in-flight set without one id, and the same set when it was never in it. */
function withoutResolvingAction(
  resolving: ReadonlySet<string>,
  stagedActionId: string
): ReadonlySet<string> {
  if (!resolving.has(stagedActionId)) return resolving;

  const remaining = new Set(resolving);
  remaining.delete(stagedActionId);
  return remaining;
}

/** A card's previous failure, cleared because it is being answered again. */
function withoutResolutionFailure(
  failures: ResolutionFailures,
  stagedActionId: string
): ResolutionFailures {
  if (failures[stagedActionId] === undefined) return failures;

  const remaining = { ...failures };
  delete remaining[stagedActionId];
  return remaining;
}

// #region Styled Components
const MainContainer = styled('div')<{
  dragging: boolean,
  isMobile: boolean,
  leftPos: number,
  topPos: number,
  transformX?: number,
  transformY?: number,
  scaleX?: number,
  scaleY?: number
}>(({ dragging, isMobile, leftPos, topPos, transformX, transformY, scaleX, scaleY, theme }) => ({
  position: 'fixed',
  zIndex: 2000,
  ...(isMobile ? {
    bottom: theme.spacing(1),
    right: theme.spacing(1),
  } : {
    left: `${leftPos}px`,
    top: `${topPos}px`,
    transform: `translate3d(${transformX ?? 0}px, ${transformY ?? 0}px, 0) scale(${dragging ? 1.05 : scaleX}, ${dragging ? 1.05 : scaleY})`,
  }),
}));
const ChatBubble = styled(Fab, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({ isMobile, theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: '4px solid transparent',
  backgroundImage: 'linear-gradient(90deg,rgb(255, 255, 255) 0%, #bbdefb 100%), linear-gradient(90deg, #b3b9ff,rgba(0, 149, 255, 0.75))',
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  color: theme.palette.primary.main,
  ...(isMobile ? {
    borderRadius: '50%',
    height: theme.spacing(6),
    minHeight: theme.spacing(6),
    minWidth: theme.spacing(6),
    padding: 0,
    width: theme.spacing(6),
  } : {}),
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #fff 0%, #e3f2fd 100%), linear-gradient(90deg,#0095ff, #0095ff)',
  },
}));
const ChatBubbleContent = styled('span')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-evenly',
  marginLeft: theme.spacing(1),
  minWidth: '110px'
}));
const ChatBubbleIcon = styled(
  'span',
  { shouldForwardProp: (prop) => prop !== 'rotate' && prop !== 'top' }
)<{ rotate: boolean, top: boolean }>(({ rotate, top }) => {
  let rotation: string;
  if (rotate) {
    rotation = top ? '90deg' : '-90deg';
  } else {
    rotation = '0deg';
  }
  return {
    color: 'rgb(0, 149, 255)',
    display: 'inline-flex',
    transition: 'transform 0.3s ease',
    transform: `rotate(${rotation})`,
    transformOrigin: 'center center',
  };
});
const ChatBubbleLabel = styled('span')(({ theme }) => ({
  display: 'inline-block',
  marginLeft: theme.spacing(0.5),
  whiteSpace: 'nowrap',
}));
const PopperContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow: 'rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;',
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  height: '550px',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  minHeight: '550px',
  maxHeight: '75vh',
  minWidth: '450px',
  maxWidth: '80vw',
  overflow: 'hidden',
  padding: 0,
  position: 'relative',
  width: '450px',
  [theme.breakpoints.down('sm')]: {
    height: 'calc(100vh - 136px)',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    maxHeight: 'calc(100vh - 136px)',
    maxWidth: 'calc(100vw - 32px)',
    minHeight: 0,
    minWidth: 0,
    width: 'calc(100vw - 32px)',
  },
}));
const DragHandle = styled(DragIndicatorIcon, {
  shouldForwardProp: (prop) => prop !== 'dragging'
})<{ dragging: boolean }>(({ dragging }) => ({
  cursor: dragging ? "grabbing" : "grab"
}));
const TopLeftResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: 0,
  left: 0,
  width: theme.spacing(2),
  height: theme.spacing(2),
  zIndex: 10,
}));
const TopResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: 0,
  left: theme.spacing(2),
  width: 'calc(100% - 32px)',
  height: theme.spacing(2),
  zIndex: 10,
}));
const TopRightResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: 0,
  right: 0,
  width: theme.spacing(2),
  height: theme.spacing(2),
  zIndex: 10,
}));
const RightResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: theme.spacing(2),
  right: 0,
  width: theme.spacing(2),
  height: 'calc(100% - 32px)',
  zIndex: 10,
}));
const BottomRightResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: theme.spacing(2),
  height: theme.spacing(2),
  zIndex: 10,
}));
const BottomResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  bottom: 0,
  left: theme.spacing(2),
  width: `calc(100% - ${theme.spacing(4)})`,
  height: theme.spacing(2),
  zIndex: 10,
}));
const BottomLeftResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: theme.spacing(2),
  height: theme.spacing(2),
  zIndex: 10,
}));
const LeftResizeHandle = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: theme.spacing(2),
  left: 0,
  width: theme.spacing(2),
  height: `calc(100% - ${theme.spacing(4)})`,
  zIndex: 10,
}));
const Header = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.primary.main,
  position: 'relative',
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
  color: theme.palette.common.white,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: theme.spacing(1),
  'h3': {
    fontWeight: 500,
  }
}));
const Content = styled('div')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  paddingRight: theme.spacing(1),
  overflowX: 'hidden',
  overflowY: 'scroll',
  scrollbarGutter: 'stable',
}));
const MessageEditor = styled(TextField)(() => ({
  backgroundColor: '#f5f5f5',
  borderRadius: '0 0 8px 8px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '0 0 8px 8px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
const Message = styled('div')<{ sent: boolean }>(
  ({ theme, sent }) => ({
    containerType: 'inline-size',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: sent ? 'row-reverse' : 'row',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& .message-text': {
      backgroundColor: sent ? '#bdf' : '#f0f0f0',
      '@container (min-width: 0px)': {
        maxWidth: '90%'
      },
      '@container (min-width: 600px)': {
        maxWidth: '75%'
      },
      '@container (min-width: 800px)': {
        maxWidth: '66%'
      }
    },
  }));
const MessageIcon = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  marginTop: theme.spacing(1),
}));
const MessageText = styled('div')(({ theme }) => ({
  backgroundColor: '#f0f0f0',
  borderRadius: '8px',
  color: theme.palette.text.primary,
  fontSize: '0.9rem',
  lineHeight: '1.5',
  marginBottom: theme.spacing(2),
  wordBreak: 'break-word',
  padding: theme.spacing(1.5, 2),
  "& p": {
    margin: 0,
  }
}));
/**
 * A turn the assistant could not answer.
 *
 * Every reply is HTTP 200 carrying prose, and a failed one carries no citations - which is
 * exactly what a correct abstention looks like. Without a visible difference, "an error
 * occurred" reads as an answer, and the one action that helps (send it again) looks like
 * arguing with the assistant.
 */
const FailedMessageText = styled(MessageText)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.contrastText,
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));
/**
 * The caveat on a turn the assistant did not finish.
 *
 * A warning beside the text rather than the error styling a failed turn gets: the text is
 * real and worth reading, it is just not all of the answer. Styled as a caveat so it is not
 * mistaken for an error, and marked `role="status"` so a screen reader announces it with the
 * message instead of interrupting as an alert would.
 */
const IncompleteNotice = styled('div')(({ theme }) => ({
  alignItems: 'flex-start',
  backgroundColor: theme.palette.warning.light,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.warning.contrastText,
  display: 'flex',
  fontSize: '0.75rem',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
}));
/**
 * Said where a replayed transcript's prepared work used to be offered.
 *
 * Quieter than `IncompleteNotice` on purpose - nothing has gone wrong, and the reader is being
 * told why a control the answer above refers to is not there rather than warned about anything.
 */
const ReplayedActionNotice = styled('div')(({ theme }) => ({
  alignItems: 'flex-start',
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  display: 'flex',
  fontSize: '0.75rem',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
}));
/**
 * Holds the turning ring and the Stop button, in that order and side by side.
 *
 * They were concentric, the ring turning behind the button. Two round things sharing a centre
 * read as one thickened control rather than as a state and a thing to press, and the ring's
 * stroke crossed the icon's own outline. Sat next to each other they are legible as two.
 */
const SendingControls = styled('span')(({ theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  gap: theme.spacing(0.5),
}));
const SendingSpinner = styled(CircularProgress)({
  // Out of the button's way: a spinner that swallowed the click would be the same defect as
  // the disabled button it replaced.
  pointerEvents: 'none',
});
/**
 * Said where a stopped turn's answer would have been.
 *
 * Not a message bubble. The assistant did not say this and must not appear to have - it is
 * the panel reporting what the user just did, which is why it reads as a note and not as a turn.
 */
const StoppedNotice = styled('div')(({ theme }) => ({
  alignItems: 'center',
  alignSelf: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  fontSize: '0.75rem',
  gap: theme.spacing(0.5),
  margin: theme.spacing(1, 0),
}));
/**
 * The panel's one live region, heard and never seen.
 *
 * Mounted for as long as the panel is open and empty most of that time, because a live region
 * has to be in the document *before* its content changes for the change to be spoken. Inserting
 * a new element that carries `aria-live` announces nothing reliably, which is why the animated
 * "Responding..." bubble and the answer bubbles cannot do this job themselves.
 *
 * `role="log"` rather than `status`: this is the running record of a conversation, and the panel
 * already uses `status` for standing notices - a second one would make `getByRole('status')`
 * ambiguous for anything reading the panel, tests included.
 *
 * Hidden the long way round rather than with `display: none` or `visibility: hidden`, either of
 * which takes the element out of the accessibility tree and silences it.
 */
const LiveAnnouncement = styled('div')({
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: '1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
});
const Timestamp = styled('div')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.6rem',
  marginTop: theme.spacing(0.5),
  width: '100%'
}));
const MarkdownStyles = styled('div')(({ theme }) => ({
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& code': {
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    padding: '2px 4px',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  '& pre': {
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    padding: '10px',
    overflowX: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: '10px',
    color: theme.palette.text.secondary,
    margin: '10px 0',
  },
  '& ul, & ol': {
    paddingLeft: '20px',
    margin: '10px 0',
    '& li': {
      marginBottom: '5px',
    },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '10px 0',
    fontWeight: 'bold',
  },
  '& strong': {
    fontWeight: 'bold',
  },
  '& em': {
    fontStyle: 'italic',
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: '20px 0',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '4px',
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: theme.spacing(1),
    '& th, & td': {
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      fontSize: '0.9em',
      padding: '8px',
      textAlign: 'left',
      overflowWrap: 'break-word',
      wordBreak: 'normal',
      whiteSpace: 'normal',
    },
    '& th': {
      backgroundColor: theme.palette.background.default,
      fontSize: '0.8em',
      fontWeight: 'bold',
    },
  },
}));
/**
 * The controls in the panel's title bar.
 *
 * One positioned box holding a row, rather than each button positioning itself. Both buttons
 * used to be the same styled component, each `position: absolute; right: 8px` - so they were
 * laid on top of one another and the pair read as a single smudged glyph. The title stays
 * centered because this box is out of the flex flow, which is what the absolute positioning
 * was for.
 */
const HeaderActions = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(0.5),
  position: 'absolute',
  right: theme.spacing(1),
}));
const HeaderIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
}));
// #endregion Styled Components

/**
 * The positioning engine's handle, the one `popperRef` hands back.
 *
 * Read off MUI's own prop type rather than imported from `@popperjs/core`: that package is a
 * dependency of MUI's, not one this app declares, so its types are not ours to import.
 */
type PopperHandle = Extract<
  NonNullable<PopperProps['popperRef']>,
  { current: unknown }
>['current'];

function PopperPlacementSync({
  placement,
  onChange,
}: {
  placement: PopperPlacementType;
  onChange: (p: PopperPlacementType) => void;
}) {
  React.useEffect(() => {
    onChange(placement);
  }, [placement, onChange]);
  return null;
}

interface AiAssistantProps {
  isMobile?: boolean;
  leftPos?: number;
  topPos?: number;
  forwardedRef?: React.Ref<HTMLDivElement>;
}

const AiAssistantSession = React.forwardRef<HTMLDivElement, AiAssistantProps>(
  function AiAssistantSession(props, forwardedRef) {
    const isMobile = props.isMobile ?? false;
    const leftPos = props.leftPos ?? 0;
    const topPos = props.topPos ?? 0;

    const containerRef = React.useRef<HTMLDivElement>(null);
    const bottomRef = React.useRef<HTMLDivElement>(null);
    const messageEditorInputRef = React.useRef<HTMLInputElement>(null);
    /**
     * The prepared action's card, when a turn has just put a decision in front of the user.
     *
     * Held by the parent rather than focused by the card on mount, because the card mounts
     * again every time the panel is reopened - a Popper unmounts its children when it closes -
     * and landing on last week's unanswered preview is not what "focus the new decision" means.
     */
    const decisionCardRef = React.useRef<HTMLDivElement>(null);
    /**
     * Names the panel for assistive technology, which is what makes `role="dialog"` worth
     * having: an unnamed dialog is announced as "dialog" and nothing else.
     */
    const panelTitleId = React.useId();

    const [containerEl, setContainerEl] = React.useState<HTMLDivElement | null>(null);
    const [popperAnchorEl, setPopperAnchorEl] = React.useState<null | HTMLElement>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const [popperPlacement, setPopperPlacement] = React.useState<PopperPlacementType>('top-end');
    const [inputText, setInputText] = React.useState<string>('');
    const [isSending, setIsSending] = React.useState<boolean>(false);
    const [respondingDelayElapsed, setRespondingDelayElapsed] = React.useState<boolean>(false);
    const [messages, setMessages] = React.useState<AssistantResponse[]>([]);
    const [conversationId, setConversationId] = React.useState<string | undefined>();
    const [dotCount, setDotCount] = React.useState<number>(1);
    /**
     * Whether this panel has already tried to load the user's last conversation.
     *
     * Tried, not succeeded. A second attempt would re-fetch a conversation the user may have
     * deliberately cleared, and would fight whatever they have typed since.
     *
     * A ref rather than state: nothing renders differently for knowing it, and setting state
     * inside the effect that reads it is a cascading render the `react-hooks/set-state-in-effect`
     * rule rightly objects to.
     */
    const historyHydratedRef = React.useRef<boolean>(false);
    const [historyNotice, setHistoryNotice] = React.useState<string | undefined>();
    /**
     * The turn in flight, kept only so it can be stopped.
     *
     * A ref rather than state: nothing renders differently for holding it - `isSending` is what
     * the Stop control keys off - and putting a mutation handle in state would re-render the
     * panel for a value nothing reads during render.
     */
    const pendingTurnRef = React.useRef<{ abort: () => void } | undefined>(undefined);
    const [stoppedNotice, setStoppedNotice] = React.useState<string | undefined>();
    /**
     * The prepared action the last turn staged, until something answers it or the panel closes.
     *
     * State rather than a ref because the render reads it - it decides which card gets
     * `decisionCardRef` - and it is set from an event handler, never from an effect.
     */
    const [decisionToFocus, setDecisionToFocus] = React.useState<string | undefined>();
    /**
     * Whether the panel has been opened, so that closing it can put focus back on the bubble
     * without a first render - where nothing has been opened and nothing was taken - stealing
     * focus from whatever the page was doing.
     */
    const hasBeenOpenedRef = React.useRef<boolean>(false);

    const trimmedInputLength = inputText.trim().length;
    const isMessageEmpty: boolean = trimmedInputLength === 0;
    const isMessageTooLong = trimmedInputLength > MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS;
    const messageValidationError = isMessageTooLong
      ? `Messages must be ${MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS.toLocaleString()} characters or fewer.`
      : undefined;
    const canSendMessage = !isMessageEmpty && !isMessageTooLong;
    const showResponding = isSending && respondingDelayElapsed;
    const announcement = announcementFor(messages, showResponding);

    const session = useSession();
    const location = useLocation();
    const [sendAssistantMessage] = useSendAssistantMessageMutation();
    const [confirmStagedAction] = useConfirmStagedActionMutation();
    const [cancelStagedAction] = useCancelStagedActionMutation();
    // Skipped until the panel is open. A bubble nobody has clicked should not be reading a
    // user's assistant history.
    const { data: conversationSummaries } = useListAssistantConversationsQuery(undefined, {
      skip: !isOpen,
    });
    const [readConversation] = useLazyReadAssistantConversationQuery();
    const [deleteAllConversations] = useDeleteAllAssistantConversationsMutation();
    const confirm = useConfirmDialog();
    const [resolvingActionIds, setResolvingActionIds] = React.useState<ReadonlySet<string>>(
      () => new Set<string>()
    );
    const [resolutionFailures, setResolutionFailures] = React.useState<ResolutionFailures>({});
    /**
     * Every prepared action this panel has sent a *confirmation* for, whatever became of it.
     *
     * A ref rather than state: nothing renders differently for knowing it. It is read when a
     * new turn retires the previews the user left alone, and a confirmed card is not one of
     * those - see `appendTurn`. Ids are never removed, because "I approved that one" does not
     * stop being true.
     *
     * Cancellations are not recorded here. They were, and it retained a card the server had
     * already superseded: a cancel that fails is the one resolution the panel offers again, so
     * a user whose cancel dropped and who then said something else was left with a stale card
     * still offering Confirm for an action nothing could answer any more.
     */
    const confirmedActionIdsRef = React.useRef<Set<string>>(new Set<string>());
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id: 'draggable',
    });
    /**
     * The open panel's positioning engine, kept so the panel can be told the bubble has moved.
     *
     * A ref rather than state: nothing renders differently for holding it, and the panel is
     * repositioned by calling into it, not by re-rendering.
     */
    const popperRef = React.useRef<PopperHandle>(null);

    const scrollToBottom = (delayMs: number = 0) => {
      if (!bottomRef.current) return;
      setTimeout(() => {
        bottomRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: "nearest",
          inline: "nearest"
        });
      }, delayMs);
    };

    const handleContainerRef = React.useCallback((node: HTMLDivElement | null) => {
      containerRef.current = node;
      setContainerEl(node);
    }, []);

    /**
     * Holds the composer, and puts the caret in it the moment the panel appears.
     *
     * A callback ref rather than an effect keyed on `isOpen`, because the panel renders through
     * a `Portal` and a portal's children are not in the document on the commit that opens it -
     * an effect can therefore run with nothing to focus. This runs when the field itself mounts,
     * which is once per opening, since the Popper unmounts its children when it closes.
     *
     * Stable on purpose: an inline callback would be invoked again on every commit and drag the
     * caret back to the start of whatever the user was typing.
     */
    const handleComposerRef = React.useCallback((node: HTMLInputElement | null) => {
      messageEditorInputRef.current = node;
      node?.focus();
    }, []);

    /** Holds the card of the decision this turn staged, for the effect below to focus. */
    const captureDecisionCard = React.useCallback((element: HTMLDivElement | null) => {
      decisionCardRef.current = element;
    }, []);

    /**
     * Keeps an open panel over the bubble it belongs to while the bubble is dragged.
     *
     * Popper measures the anchor once and then only re-measures on scroll and resize. Dragging
     * moves the bubble with a CSS transform and drops it by moving this container's `left` and
     * `top`, and neither fires either event - so the panel stayed behind at the spot the bubble
     * was in when it was opened, ending up detached from it and often off screen.
     *
     * A layout effect, and `forceUpdate` rather than `update`: both halves of measuring before
     * the browser paints. A passive effect runs after the paint that already moved the bubble,
     * and `update` defers to a microtask, so either one leaves the panel a frame behind the
     * bubble for the whole drag. Re-measuring in the same commit as the transform is what keeps
     * the two moving as one thing.
     */
    React.useLayoutEffect(() => {
      popperRef.current?.forceUpdate();
    }, [
      leftPos,
      topPos,
      isDragging,
      transform?.x,
      transform?.y
    ]);

    React.useEffect(() => {
      let timeout: ReturnType<typeof setTimeout>;

      if (isSending) {
        timeout = setTimeout(() => {
          setRespondingDelayElapsed(true);
        }, 1000);
      }

      return () => {
        clearTimeout(timeout);
      };
    }, [
      isSending
    ]);

    React.useEffect(() => {
      if (showResponding) {
        const interval = setInterval(() => {
          setDotCount(prev => (prev % 3) + 1); // Cycle through 1, 2, 3
        }, 500); // Change every 500ms

        return () => clearInterval(interval);
      }
    }, [
      showResponding
    ]);

    /**
     * Picks up where the user left off, once, when the panel is first opened.
     *
     * The most recent conversation rather than a picker: the panel is a single-thread bubble and
     * continuing the last thread is what "survives a refresh" means here. A list view over
     * `conversationSummaries` is the shape this grows into.
     */
    React.useEffect(() => {
      if (!isOpen || historyHydratedRef.current || conversationSummaries === undefined) return;

      historyHydratedRef.current = true;
      const mostRecent = conversationSummaries[0];
      if (mostRecent === undefined) return;

      void hydrateConversation(
        mostRecent.conversationId,
        readConversation,
        setMessages,
        setConversationId
      );
    }, [
      isOpen,
      conversationSummaries,
      readConversation
    ]);

    /**
     * Where the keyboard lands once a turn has finished arriving.
     *
     * The composer, unless the turn put a decision in front of the user - in which case the
     * prepared action's card, because a write waiting for approval is not something to leave a
     * keyboard user to find by tabbing past the whole transcript. The card and not its Confirm
     * button: landing on the button means one reflexive Enter performs the write before the
     * reader has said what it is.
     *
     * Guarded on `isOpen` because this used to run whether the panel was open or not, and a
     * reply that arrived after the user closed it took the focus off the page.
     */
    React.useEffect(() => {
      if (!isOpen) return;

      scrollToBottom(100);
      const decision = decisionCardRef.current;
      if (decision !== null) {
        decision.focus();
        return;
      }

      messageEditorInputRef.current?.focus();
    }, [
      isOpen,
      messages,
      showResponding
    ]);

    /**
     * Hands the keyboard back to the bubble when the panel closes.
     *
     * Whatever closed it - Escape, the Close button, the bubble itself - focus would otherwise
     * fall to the document body, which is where a keyboard user has to start tabbing from
     * again. The bubble is the anchor the panel opened off, so it is also the element the user
     * was on before it opened - and naming it beats restoring whatever held focus beforehand,
     * which is nothing at all when a mouse click opened the panel without moving focus.
     */
    React.useEffect(() => {
      if (isOpen) {
        hasBeenOpenedRef.current = true;
        return;
      }

      if (!hasBeenOpenedRef.current) return;
      hasBeenOpenedRef.current = false;
      popperAnchorEl?.focus();
    }, [
      isOpen,
      popperAnchorEl
    ]);

    function closePanel() {
      setIsOpen(false);
      // A decision the user walked away from is no longer the one to land on. Left set, it
      // would pull focus onto a card from the previous session the next time the panel opens,
      // instead of into the composer the user just asked for.
      setDecisionToFocus(undefined);
    }

    /**
     * Escape closes the panel, and Tab stays inside it.
     *
     * The trap is a keyboard one only, and that is the whole design. Tab used to walk straight
     * out of the open panel and into the page behind it, leaving a keyboard user somewhere they
     * cannot see - the panel floats over the page and gives no sign of where focus went. What
     * the panel deliberately does not do is make that page inert: it has no backdrop, the bubble
     * it hangs off can still be dragged, and a mouse can still reach anything underneath. That
     * is why it is `aria-modal="false"` - see the panel itself - and why nothing here fights a
     * click that lands outside.
     *
     * Escape is the way out for a keyboard, and it hands focus back to the bubble, so the cycle
     * has an exit that does not require a mouse.
     */
    function handlePanelKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
      if (event.key === KeyboardKey.Escape) {
        // The panel is what the Escape was aimed at. Left to bubble, it would also reach
        // whatever the panel is floating over.
        event.stopPropagation();
        closePanel();
        return;
      }

      if (event.key !== KeyboardKey.Tab) return;

      const container = containerRef.current;
      if (container === null) return;

      const tabbable = tabbableWithin(container);
      if (tabbable.length === 0) return;

      const first = tabbable[0];
      const last = tabbable[tabbable.length - 1];
      const focused = container.ownerDocument.activeElement;

      // The panel itself and a prepared action's card are focusable but not tabbable, so a Tab
      // from either has no "next" of its own to move to - it goes to the top of the cycle.
      const atStart = focused === first || focused === container;
      if (event.shiftKey && atStart) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && focused === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function handleTogglePopper(event: React.MouseEvent<HTMLElement>) {
      if (isDragging) return;
      if (isOpen) {
        closePanel();
      } else {
        setPopperAnchorEl(event.currentTarget);
        setIsOpen(true);
      }
    }

    async function handleSendMessage() {
      const trimmedMessage = inputText.trim();
      if (!trimmedMessage) {
        console.warn('Message cannot be empty');
        return;
      }
      if (trimmedMessage.length > MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS) return;

      const confirmedActionIds = confirmedActionIdsRef.current;

      try {
        setRespondingDelayElapsed(false);
        setDotCount(1);
        setIsSending(true);
        setInputText('');
        setStoppedNotice(undefined);
        // Whatever the last turn staged, this one is not it. Cleared before the request rather
        // than after it, so a turn that stages nothing leaves the keyboard in the composer.
        setDecisionToFocus(undefined);

        setMessages(prev => [
          ...prev,
          AssistantResponse.create({
            conversationId: undefined,
            role: Role.User,
            text: trimmedMessage,
            citations: [],
            timestamp: new AHDate()
          })
        ]);

        // Held rather than awaited inline, because the handle is what `abort()` lives on and
        // the promise alone cannot be stopped.
        const turn = sendAssistantMessage({
          message: trimmedMessage,
          conversationId,
          currentRoute: location.pathname
        });
        pendingTurnRef.current = turn;

        const assistantResponse = await turn.unwrap();

        setConversationId(assistantResponse.conversationId);
        setMessages(prev => appendTurn(prev, assistantResponse, confirmedActionIds));
        setDecisionToFocus(decisionStagedBy(assistantResponse));
      } catch (error) {
        if (wasStoppedByUser(error)) {
          // Said rather than left silent. The user's own message stays where they typed it,
          // and without a word beside it a stopped turn looks like one that is still coming.
          setStoppedNotice('You stopped this response.');
        } else {
          console.error('Error in AI Assistant API Service:', error);
        }
      }

      pendingTurnRef.current = undefined;

      // Ran as a `finally` clause until the React Compiler, which cannot lower one, started skipping
      // this component. Nothing follows the try statement, so running it here is equivalent.
      setIsSending(false);
      setRespondingDelayElapsed(false);
    }

    /**
     * Stops the turn in flight.
     *
     * Aborting the request is what stops the work, not just the waiting: the browser drops the
     * connection, ASP.NET Core fires `HttpContext.RequestAborted`, and the token that reaches
     * the retrieval call, the tool loop and the citation judge is that one. The turn is
     * recorded in a single save at the end, so a stopped one records nothing rather than half
     * of something.
     */
    function handleStopResponding() {
      pendingTurnRef.current?.abort();
    }

    async function handleClearHistory() {
      const response = await confirm({
        title: 'Delete your assistant conversations?',
        message:
          'Every conversation you have had with the assistant will be deleted, along with the '
          + 'excerpts it quoted. This cannot be undone.',
        okButtonText: 'Delete',
      });

      if (response !== ConfirmResponse.Ok) return;

      setHistoryNotice(undefined);
      // The stopped notice describes a turn that is about to be erased. Left standing it sits
      // under an empty transcript, telling the user they stopped a response whose message is no
      // longer in the panel, until they happen to send another one.
      setStoppedNotice(undefined);
      await clearAssistantHistory(
        deleteAllConversations,
        setMessages,
        setConversationId,
        setHistoryNotice
      );
    }

    function beginResolving(stagedActionId: string) {
      setResolvingActionIds(previous => new Set(previous).add(stagedActionId));
      // Whatever the last attempt said about this card is stale the moment another starts, and
      // leaving it beside a spinner reads as the new attempt having failed as well.
      setResolutionFailures(previous => withoutResolutionFailure(previous, stagedActionId));
    }

    function handleConfirmStagedAction(stagedActionId: string) {
      // Before the request, not after it: a confirmation whose answer never arrives is exactly
      // the one whose card must not be relabelled by a later turn.
      confirmedActionIdsRef.current.add(stagedActionId);
      beginResolving(stagedActionId);
      void resolveStagedAction(
        confirmStagedAction,
        stagedActionId,
        StagedActionIntent.Confirm,
        setMessages,
        setResolvingActionIds,
        setResolutionFailures
      );
    }

    function handleCancelStagedAction(stagedActionId: string) {
      beginResolving(stagedActionId);
      void resolveStagedAction(
        cancelStagedAction,
        stagedActionId,
        StagedActionIntent.Cancel,
        setMessages,
        setResolvingActionIds,
        setResolutionFailures
      );
    }

    const getPopperTransformOrigin = (placement: PopperPlacementType) => {
      switch (placement) {
        case 'top-end':
          return 'right bottom';
        case 'top-start':
          return 'left bottom';
        case 'bottom-end':
          return 'right top';
        case 'bottom-start':
          return 'left top';
        default:
          return 'center center';
      }
    };

    return (
      <MainContainer
        ref={forwardedRef}
        dragging={isDragging}
        isMobile={isMobile}
        leftPos={leftPos}
        topPos={topPos}
        transformX={transform?.x}
        transformY={transform?.y}
        scaleX={transform?.scaleX}
        scaleY={transform?.scaleY}
        {...attributes}
      >
        <Popper
          anchorEl={popperAnchorEl}
          open={isOpen}
          placement="top-end"
          popperRef={popperRef}
          sx={{ zIndex: 2000 }}
          transition
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 16
              }
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-end', 'top-start', 'bottom-start'],
                padding: 8
              }
            }
          ]}
        >
          {({ TransitionProps, placement }) => {
            return (
              <ResizeHandlePlacementContext.Provider value={placement}>
                <PopperPlacementSync placement={placement} onChange={setPopperPlacement} />
                <Grow
                  {...TransitionProps}
                  timeout={300}
                  style={{
                    transformOrigin: getPopperTransformOrigin(placement)
                  }}
                  onEntered={() => scrollToBottom(0)}
                >
                  {/*
                    A dialog, and honestly a non-modal one. `role="dialog"` and a name are what
                    let a screen reader say what this floating thing is and where its boundary
                    lies; `aria-modal="false"` is the truth beside them, because the panel puts
                    up no backdrop and leaves the page behind it clickable. Claiming modality
                    would tell a reader that page no longer exists while a mouse could still
                    reach every control on it.

                    `tabIndex={-1}` so focus can be sent here without putting the panel in the
                    tab cycle.
                  */}
                  <PopperContainer
                    ref={handleContainerRef}
                    role="dialog"
                    aria-modal="false"
                    aria-labelledby={panelTitleId}
                    tabIndex={-1}
                    onKeyDown={handlePanelKeyDown}
                  >
                    <ResizeHandle
                      handle="topLeft"
                      containerEl={containerEl}
                    >
                      <TopLeftResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="top"
                      containerEl={containerEl}
                    >
                      <TopResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="topRight"
                      containerEl={containerEl}
                    >
                      <TopRightResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="right"
                      containerEl={containerEl}
                    >
                      <RightResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="bottomRight"
                      containerEl={containerEl}
                    >
                      <BottomRightResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="bottom"
                      containerEl={containerEl}
                    >
                      <BottomResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="bottomLeft"
                      containerEl={containerEl}
                    >
                      <BottomLeftResizeHandle />
                    </ResizeHandle>
                    <ResizeHandle
                      handle="left"
                      containerEl={containerEl}
                    >
                      <LeftResizeHandle />
                    </ResizeHandle>
                    <Header>
                      <h3 id={panelTitleId}>AI Assistant</h3>
                      <HeaderActions>
                        <Tooltip title="Delete your conversations">
                          <HeaderIconButton
                            aria-label="Delete your assistant conversations"
                            onClick={() => { void handleClearHistory(); }}
                            size="medium"
                          >
                            <DeleteSweepIcon fontSize='medium' />
                          </HeaderIconButton>
                        </Tooltip>
                        <Tooltip title="Close chat">
                          <HeaderIconButton
                            aria-label="Close chat"
                            onClick={closePanel}
                            size="medium"
                          >
                            <CloseIcon fontSize='medium' />
                          </HeaderIconButton>
                        </Tooltip>
                      </HeaderActions>
                    </Header>
                    <LiveAnnouncement
                      role="log"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {announcement}
                    </LiveAnnouncement>
                    <Content>
                      <Message sent={false}>
                        <MessageIcon>
                          <AssistantIcon fontSize="large" />
                        </MessageIcon>
                        <MessageText className="message-text">
                          {session.user?.name.firstName ? `${new AHDate().TimeOfDayGreeting}, ${session.user?.name.firstName}! ` : "Hello! "}
                          How can I help?
                        </MessageText>
                      </Message>
                      {messages.map((response: AssistantResponse, messageIndex: number) => (
                        <Message
                          sent={response.role === Role.User}
                          key={`${response.timestamp.toISOString()}-${messageIndex}`}
                        >
                          <MessageIcon>
                            {response.role === Role.User ? null : <AssistantIcon fontSize="large" />}
                          </MessageIcon>
                          {response.showsAnswerText ? (
                            <MessageText className="message-text">
                              <MarkdownStyles>
                                <CitedAnswer
                                  text={response.text}
                                  citations={response.citations}
                                  grounding={response.grounding}
                                />
                              </MarkdownStyles>
                              {response.isIncompleteAnswer && (
                                <IncompleteNotice role="status">
                                  <ErrorOutlineIcon fontSize="small" />
                                  <span>
                                    The assistant ran out of steps before finishing this.
                                    Treat it as partial, and try asking for one piece at a time.
                                  </span>
                                </IncompleteNotice>
                              )}
                              {/*
                                Deliberately not the wording above. That turn met the model's own
                                limit and a narrower question genuinely helps; this one met a
                                limit the server sets, which no rephrasing gets past - so telling
                                the user to try smaller pieces would send them round the same loop
                                to the same place.
                              */}
                              {response.isExhaustedAnswer && (
                                <IncompleteNotice role="status">
                                  <ErrorOutlineIcon fontSize="small" />
                                  <span>
                                    This ran past the work the assistant is allowed to do in one
                                    go, so what is above is partial. Asking about a narrower part
                                    of it will get further.
                                  </span>
                                </IncompleteNotice>
                              )}
                              {response.actionableStagedActions.map(action => (
                                <StagedActionCard
                                  key={action.id.value}
                                  action={action}
                                  /*
                                    Only while it is still a decision. Once it is answered the
                                    card stops being the place to stand, and focus belongs back
                                    in the composer.
                                  */
                                  ref={
                                    action.isPending && action.id.value === decisionToFocus
                                      ? captureDecisionCard
                                      : undefined
                                  }
                                  busy={resolvingActionIds.has(action.id.value)}
                                  failure={resolutionFailures[action.id.value]}
                                  onConfirm={handleConfirmStagedAction}
                                  onCancel={handleCancelStagedAction}
                                />
                              ))}
                              {response.preparedActionsNotReplayed && (
                                <ReplayedActionNotice role="status">
                                  <HistoryIcon fontSize="small" />
                                  <span>
                                    This conversation is being shown from your history. Anything
                                    the assistant prepared in it is no longer offered here - ask
                                    again to prepare it afresh.
                                  </span>
                                </ReplayedActionNotice>
                              )}
                              <Timestamp>
                                {response.timestamp.format('h:mm A')}
                              </Timestamp>
                            </MessageText>
                          ) : (
                            <FailedMessageText className="message-text" role="alert">
                              <ErrorOutlineIcon fontSize="small" />
                              <div>
                                {response.text}
                                <Timestamp>
                                  {response.timestamp.format('h:mm A')}
                                </Timestamp>
                              </div>
                            </FailedMessageText>
                          )}
                        </Message>
                      ))}
                      {historyNotice !== undefined && (
                        <Message sent={false}>
                          <MessageIcon>
                            <AssistantIcon fontSize="large" />
                          </MessageIcon>
                          <MessageText className="message-text" role="status">
                            {historyNotice}
                          </MessageText>
                        </Message>
                      )}
                      {stoppedNotice !== undefined && (
                        <StoppedNotice role="status">
                          <StopCircleIcon fontSize="small" />
                          <span>{stoppedNotice}</span>
                        </StoppedNotice>
                      )}
                      {isSending && showResponding && (
                        <Message sent={false}>
                          <MessageIcon>
                            <AssistantIcon fontSize="large" />
                          </MessageIcon>
                          <MessageText className="message-text">
                            {`Responding${'.'.repeat(dotCount)}`}
                          </MessageText>
                        </Message>
                      )}
                      <div ref={bottomRef} />
                    </Content>
                    <MessageEditor
                      inputRef={handleComposerRef}
                      multiline
                      maxRows={3}
                      variant="outlined"
                      placeholder="Type your message..."
                      fullWidth
                      disabled={isSending}
                      value={inputText}
                      error={messageValidationError !== undefined}
                      helperText={messageValidationError}
                      onKeyDown={(event) => {
                        if (event.key === KeyboardKey.Enter && !event.shiftKey && !event.nativeEvent.isComposing) {
                          event.preventDefault();
                          if (canSendMessage) {
                            void handleSendMessage();
                          }
                        }
                      }}
                      onChange={(e) => setInputText(e.target.value)}
                      slotProps={{
                        htmlInput: {
                          maxLength: MAXIMUM_ASSISTANT_MESSAGE_CHARACTERS,
                        },
                        input: {
                          autoComplete: "off",
                          endAdornment: isSending ? (
                            // The spinner used to sit inside a disabled Send button, so the one
                            // moment a user most wants a way out was the one moment the only
                            // control was inert. It still turns - beside the button rather than
                            // in place of it - so the turn reads as running and stoppable at once.
                            <SendingControls>
                              <SendingSpinner
                                color="inherit"
                                disableShrink
                                size={20}
                              />
                              <Tooltip title="Stop responding">
                                <IconButton
                                  aria-label="Stop responding"
                                  edge="end"
                                  color="primary"
                                  onClick={handleStopResponding}
                                >
                                  <StopCircleIcon />
                                </IconButton>
                              </Tooltip>
                            </SendingControls>
                          ) : (
                            <IconButton
                              aria-label="Send message"
                              edge="end"
                              color="primary"
                              disabled={!canSendMessage}
                              onClick={handleSendMessage}
                            >
                              <SendIcon />
                            </IconButton>
                          )
                        }
                      }}
                    />
                  </PopperContainer>
                </Grow>
              </ResizeHandlePlacementContext.Provider>
            );
          }}
        </Popper>
        <ChatBubble
          ref={setNodeRef}
          aria-label="Chat with AI Assistant"
          color="primary"
          disableRipple
          isMobile={isMobile}
          title={isOpen ? "Close Chat" : "Chat with AI Assistant"}
          variant="extended"
          onClick={handleTogglePopper}
        >
          {!isMobile && <DragHandle dragging={isDragging} {...listeners} />}
          {isMobile ? (
            <ChatBubbleIcon rotate={isOpen} top={popperPlacement?.includes('top') ?? false}>
              {isOpen ? <LastPageIcon /> : <AssistantIcon />}
            </ChatBubbleIcon>
          ) : (
            <ChatBubbleContent>
              <ChatBubbleIcon rotate={isOpen} top={popperPlacement?.includes('top') ?? false}>
                {isOpen ? <LastPageIcon /> : <AssistantIcon />}
              </ChatBubbleIcon>
              <ChatBubbleLabel>
                {isOpen ? "Minimize" : "Assistant"}
              </ChatBubbleLabel>
            </ChatBubbleContent>
          )}
        </ChatBubble>
      </MainContainer>
    )
  });

const AiAssistant = React.forwardRef<HTMLDivElement, AiAssistantProps>(
  function AiAssistant(props, forwardedRef) {
    const session = useSession();
    const sessionKey = `${session.user?.id?.value ?? ''}:${session.user?.legalEntityId?.value ?? ''}`;

    return <AiAssistantSession key={sessionKey} ref={forwardedRef} {...props} />;
  }
);

export default AiAssistant;
